/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, Popper, Typography } from '@mui/material';

interface Props {
  title: string;
  children: React.ReactNode;
  popperProps?: React.ComponentProps<typeof Popper>;
  buttonProps?: React.ComponentProps<typeof Button>;
}

export const ViewMorePopper = ({ title, children, popperProps, buttonProps }: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'view-more-popper' : undefined;

  return (
    <Box sx={{ display: 'inline-block' }}>
      <Button
        aria-describedby={id}
        variant="text"
        onClick={handleClick}
        disableRipple
        data-testid={`${title.toLowerCase()}-view-more-button`}
        sx={{
          color: 'link.main',
          fontWeight: 800,
          textDecoration: 'underline',
          lineHeight: '16px',
          textTransform: 'none',
          py: 0,
          fontSize: { xs: 12, sm: 14 },
        }}
        {...buttonProps}
      >
        View More
      </Button>
      <Popper
        id={id}
        placement="top"
        open={open}
        anchorEl={anchorEl}
        sx={{
          boxShadow: 3,
          padding: 3,
          backgroundColor: 'background.paper',
          maxWidth: 400,
          minWidth: 150,
          borderRadius: 1,
          color: 'text.primary',
          zIndex: 10000,
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
        {...popperProps}
      >
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 14, sm: 16 },
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>
            <IconButton
              onClick={handleClick}
              sx={{ mt: -1, ml: 1, color: 'text.primary' }}
              edge="end"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              fontSize: { xs: 12, sm: 14 },
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {children}
          </Box>
        </Box>
      </Popper>
    </Box>
  );
};
